export interface StageReason {
  reason: string;
  evidence?: string;
  // 旧报告字段，兼容渲染
  count?: number;
  pct?: number;
}

export interface StageSummary {
  stage: string;
  intervention_rate?: number;
  event_count: number;
  has_rate?: boolean;
  outputs?: number;
  top_reasons?: StageReason[];
  suggested_action?: string;
}

export interface CostSummary {
  llm_total_tokens?: number;
  llm_cost?: number;
  image_count?: number;
  image_cost?: number;
  video_seconds?: number;
  video_cost?: number;
  service_count?: number;
  service_cost?: number;
  service_credits?: number;
  services?: { name: string; count: number; cost: number; credits: number }[];
  top_steps?: { step: string; calls: number; tokens: number; cost: number }[];
}

export interface ConfigChangeEntry {
  time: string;
  type: string;
  summary: string;
  before?: { intervention_density: number; first_pass_rate: number };
  after?: { intervention_density: number; first_pass_rate: number };
  before_window?: string;
  after_window?: string;
  after_truncated?: boolean;
  comparison_scope?: 'change_set' | string;
}

export interface SummaryCardsData {
  overall_first_pass_rate?: number;
  intervention_density?: number;
  legacy_intervention_rate?: number; // 旧报告 overall_intervention_rate
  total_interventions?: number;
  shots_created?: number;
  by_stage?: StageSummary[];
  vs_previous?: {
    intervention_rate_delta?: number; // 旧报告
    first_pass_rate_delta?: number;
    intervention_density_delta?: number;
    config_change_impact?: string;
  };
  key_findings?: string[];
  cost?: CostSummary;
  config_changes?: ConfigChangeEntry[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asNumberOrZero(value: unknown): number {
  return typeof value === 'number' ? value : 0;
}

// key_findings 由 LLM 产出，逐元素窄化为 string；过滤后为空则视为缺失。
function narrowKeyFindings(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const findings = value.filter((f): f is string => typeof f === 'string');
  return findings.length > 0 ? findings : undefined;
}

// top_reasons 由 LLM 产出：reason 必须是 string，否则整条丢弃；
// evidence/count/pct 类型不符时丢弃该字段但保留元素。
function narrowTopReasons(value: unknown): StageReason[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const reasons = value.flatMap((item): StageReason[] => {
    if (!isRecord(item) || typeof item.reason !== 'string') return [];
    return [{
      reason: item.reason,
      ...(typeof item.evidence === 'string' ? { evidence: item.evidence } : {}),
      ...(typeof item.count === 'number' ? { count: item.count } : {}),
      ...(typeof item.pct === 'number' ? { pct: item.pct } : {}),
    }];
  });
  return reasons.length > 0 ? reasons : undefined;
}

function narrowByStage(value: unknown): StageSummary[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter(isRecord).map((stage) => ({
    ...(stage as unknown as StageSummary),
    top_reasons: narrowTopReasons(stage.top_reasons),
    suggested_action: typeof stage.suggested_action === 'string' ? stage.suggested_action : undefined,
  }));
}

function normalizeTopSteps(value: unknown): CostSummary['top_steps'] {
  if (!Array.isArray(value)) return undefined;
  return value.filter(isRecord).map((step) => ({
    step: typeof step.step === 'string' ? step.step : '',
    calls: asNumberOrZero(step.calls),
    tokens: asNumberOrZero(step.tokens),
    cost: asNumberOrZero(step.cost),
  }));
}

function normalizeCost(value: unknown): CostSummary | undefined {
  if (!isRecord(value)) return undefined;
  return {
    ...(value as CostSummary),
    top_steps: normalizeTopSteps(value.top_steps),
  };
}

function normalizeWindowMetrics(value: unknown): ConfigChangeEntry['before'] {
  if (!isRecord(value)) return undefined;
  return {
    intervention_density: asNumberOrZero(value.intervention_density),
    first_pass_rate: asNumberOrZero(value.first_pass_rate),
  };
}

function normalizeConfigChanges(value: unknown): ConfigChangeEntry[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter(isRecord).map((entry) => ({
    ...(entry as unknown as ConfigChangeEntry),
    before: normalizeWindowMetrics(entry.before),
    after: normalizeWindowMetrics(entry.after),
  }));
}

// summary_cards 内容来自 LLM 输出，本函数是渲染前唯一的信任边界：
// 对不受后端确定性类型保障的字段做运行时窄化/规范化，避免渲染白屏。
export function parseSummaryCards(raw: string): SummaryCardsData | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

  const data = parsed as SummaryCardsData & { overall_intervention_rate?: number };
  const normalized: SummaryCardsData = {
    ...data,
    key_findings: narrowKeyFindings(data.key_findings),
    by_stage: narrowByStage(data.by_stage),
    cost: normalizeCost(data.cost),
    config_changes: normalizeConfigChanges(data.config_changes),
  };
  if (normalized.intervention_density === undefined && data.overall_intervention_rate !== undefined) {
    return { ...normalized, legacy_intervention_rate: data.overall_intervention_rate };
  }
  return normalized;
}
